import { useEffect, useRef, useState } from 'react'
import { Button, Layout } from '@design-systems/canvas-ui'
import Icon, { BullseyeIcon, LogoutIcon } from '@design-systems/react-icons'
import { ProtectedElement } from '@praxis/component-auth'
import { useEnv } from '@praxis/component-runtime-env'
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom'
import { appRoutes } from '../../routes/routeConfig'
import { ROUTE_ASSISTANT, ROUTE_BRANDS, ROUTE_HOME } from '../../routes/routes'
import { navigationConfig } from '../../config/navigationConfig'
import { getRoleLabel, hasPermission } from '../../roleAccess/config/roleConfig'
import { useCurrentUserRole } from '../../roleAccess/hooks/useCurrentUserRole'
import { getUserSession } from '../../authentication/helpers/sessionStorage'
import './AppLayout.css'

export const getRouteElement = ({
  allowed,
  element,
  protected: isProtected,
}) => {
  if (!isProtected) return element

  return (
    <ProtectedElement allowed={allowed?.length ? allowed : undefined}>
      {element}
    </ProtectedElement>
  )
}

const getInitials = (name = '') => {
  const parts = `${name}`.trim().split(/\s+/).filter(Boolean)
  if (!parts.length) return 'AU'

  const initials = parts
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('')
  return initials || 'AU'
}

const AppLayout = ({ logout = () => {} }) => {
  const session = getUserSession()
  const fullName = session.fullName || session.displayName || ''
  const firstName = session.firstName || ''
  const lastName = session.lastName || ''
  const avatarUrl =
    session.avatarUrl ||
    session.avatar ||
    session.picture ||
    session.profileImage ||
    session.imageUrl ||
    session.image_url ||
    session.profile_image ||
    ''
  const displayName =
    fullName || `${firstName} ${lastName}`.trim() || 'Admin User'
  const initials = getInitials(displayName)
  const role = useCurrentUserRole()
  const roleLabel = getRoleLabel(role)
  const { applicationName = 'Brand DNA' } = useEnv()
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const profileMenuRef = useRef(null)
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false)
  const navigationItems = navigationConfig
    .filter((item) => !item.permission || hasPermission(role, item.permission))
    .map(({ path, permission, section, ...item }) => ({
      ...item,
      isActive: pathname === path,
    }))
  const hasToolsNavigation = navigationConfig.some(
    (item) =>
      item.section === 'Tools' &&
      (!item.permission || hasPermission(role, item.permission)),
  )

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (
        isProfileMenuOpen &&
        profileMenuRef.current &&
        !profileMenuRef.current.contains(event.target)
      ) {
        setIsProfileMenuOpen(false)
      }
    }

    document.addEventListener('mousedown', handlePointerDown)
    return () => document.removeEventListener('mousedown', handlePointerDown)
  }, [isProfileMenuOpen])

  const handleNavigationChange = (selectedItem) => {
    const destination = navigationConfig.find(
      (item) => item.id === selectedItem.id,
    )
    if (destination) navigate(destination.path)
  }

  const handleProfileToggle = () => {
    setIsProfileMenuOpen((current) => !current)
  }

  const handleLogout = () => {
    setIsProfileMenuOpen(false)
    logout()
  }

  return (
    <Layout darkMode={false}>
      <Layout.GlobalHeader
        logoSlot={
          <div className="app-layout__brand">
            <Icon size="md" color="red" svg={BullseyeIcon} />
            <strong>{applicationName}</strong>
          </div>
        }
        profileSlot={
          <div className="app-layout__profile-wrap" ref={profileMenuRef}>
            <button
              aria-label={`Open profile menu for ${displayName}`}
              className="app-layout__profile-trigger"
              onClick={handleProfileToggle}
              type="button"
            >
              <div
                aria-label={`Profile for ${displayName}`}
                className="app-layout__profile-avatar"
                role="img"
              >
                {avatarUrl ? (
                  <img alt={displayName} src={avatarUrl} />
                ) : (
                  <span>{initials}</span>
                )}
              </div>
              <span className="app-layout__profile-copy">
                <strong>{displayName}</strong>
              </span>
            </button>
            {isProfileMenuOpen && (
              <div className="app-layout__profile-menu" role="menu">
                <div className="app-layout__profile-menu-header">
                  <span className="app-layout__profile-menu-name">
                    {displayName}
                  </span>
                  <span className="app-layout__profile-menu-role">
                    {roleLabel}
                  </span>
                </div>
                <Button
                  aria-label="Logout"
                  className="app-layout__profile-menu-logout"
                  onClick={handleLogout}
                  type="button"
                  variant="ghost-neutral"
                >
                  <Icon size="md" svg={LogoutIcon} />
                  <span>Logout</span>
                </Button>
              </div>
            )}
          </div>
        }
      />
      <Layout.SideNav
        className={
          hasToolsNavigation
            ? 'app-layout__side-nav--with-tools'
            : 'app-layout__side-nav'
        }
        data-testid="app-side-nav"
        items={navigationItems}
        onChange={handleNavigationChange}
      />
      <Layout.Body>
        <Routes>
          <Route
            path={ROUTE_HOME}
            element={<Navigate to={ROUTE_ASSISTANT} replace />}
          />
          {appRoutes.map((route) => (
            <Route
              key={route.path}
              path={route.path}
              element={
                route.permission && !hasPermission(role, route.permission) ? (
                  <Navigate to={ROUTE_BRANDS} replace />
                ) : (
                  getRouteElement(route)
                )
              }
            />
          ))}
          <Route path="*" element={<Navigate to={ROUTE_BRANDS} replace />} />
        </Routes>
      </Layout.Body>
    </Layout>
  )
}

export default AppLayout
